import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { showActionErrorFromUnknown } from "@/lib/feedback";
import { Button } from "@/components/atoms/Button";
import { Drawer } from "@/components/organisms/Drawer";
import {
  buildOwnerSelectOptions,
  contactDetailsToSummary,
} from "@/lib/contactDisplay";
import { contactPropertyListScope } from "@/lib/contactPropertyListScope";
import { propertyResponseToSummary, unwrapApiData } from "@/lib/propertyDisplay";
import { branchesService } from "@/services/branchesService";
import { contactsService } from "@/services/contactsService";
import { propertiesService } from "@/services/propertiesService";
import { usersService } from "@/services/usersService";
import type { BranchSummary, ContactSummary, UserSummary } from "@/types/entities";
import { useSettingsStore } from "@/store/settingsStore";
import { PropertyFormFields } from "./PropertyFormFields";
import {
  buildPropertyCreatePayload,
  emptyPropertyFormValues,
  resolvePropertyApiContext,
  validatePropertyForm,
  type PropertyFormValues,
} from "./propertyFormUtils";

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const DROPDOWN_PAGE_SIZE = 20;

export function PropertyDrawer({ open, onClose, onSaved }: Props) {
  const { legalEntityId, branchId } = useSettingsStore();
  const [loading, setLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [branches, setBranches] = useState<BranchSummary[]>([]);
  const [branchesPage, setBranchesPage] = useState(-1);
  const [branchesHasNext, setBranchesHasNext] = useState(true);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [branchesLoaded, setBranchesLoaded] = useState(false);
  const [contactsPool, setContactsPool] = useState<ContactSummary[]>([]);
  const [contactSearchQuery, setContactSearchQuery] = useState("");
  const [contactsPage, setContactsPage] = useState(-1);
  const [contactsHasNext, setContactsHasNext] = useState(true);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [negotiators, setNegotiators] = useState<UserSummary[]>([]);
  const [negotiatorsPage, setNegotiatorsPage] = useState(-1);
  const [negotiatorsHasNext, setNegotiatorsHasNext] = useState(true);
  const [negotiatorsLoaded, setNegotiatorsLoaded] = useState(false);
  const [loadingNegotiators, setLoadingNegotiators] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [ownerContactIds, setOwnerContactIds] = useState<string[]>([""]);
  const branchesFetchGen = useRef(0);
  const contactsFetchGen = useRef(0);
  const negotiatorsFetchGen = useRef(0);
  const ownerContactIdsRef = useRef(ownerContactIds);
  const hydratedOwnerIdsRef = useRef(new Set<string>());
  ownerContactIdsRef.current = ownerContactIds;

  const mergeById = <T extends { id: string }>(prev: T[], next: T[]): T[] => {
    const map = new Map(prev.map((x) => [x.id, x]));
    for (const x of next) map.set(x.id, x);
    return [...map.values()];
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
  } = useForm<PropertyFormValues>({
    mode: "onSubmit",
    defaultValues: emptyPropertyFormValues(),
  });

  const resetDropdownState = useCallback(() => {
    branchesFetchGen.current += 1;
    contactsFetchGen.current += 1;
    negotiatorsFetchGen.current += 1;
    setBranches([]);
    setBranchesPage(-1);
    setBranchesHasNext(true);
    setBranchesLoaded(false);
    setLoadingBranches(false);
    setContactsPool([]);
    setContactSearchQuery("");
    setContactsPage(-1);
    setContactsHasNext(true);
    setContactsLoaded(false);
    setLoadingContacts(false);
    setNegotiators([]);
    setNegotiatorsPage(-1);
    setNegotiatorsHasNext(true);
    setNegotiatorsLoaded(false);
    setLoadingNegotiators(false);
  }, []);

  const handleClose = useCallback(() => {
    reset(emptyPropertyFormValues());
    setOwnerContactIds([""]);
    setSubmitAttempted(false);
    resetDropdownState();
    onClose();
  }, [onClose, reset, resetDropdownState]);

  const fetchBranchesPage = useCallback(
    async (page: number) => {
      if (loadingBranches) return;
      const gen = ++branchesFetchGen.current;
      setLoadingBranches(true);
      try {
        const res = await branchesService.list({}, { page, size: DROPDOWN_PAGE_SIZE });
        if (gen !== branchesFetchGen.current) return;
        setBranches((prev) =>
          page === 0 ? res.content ?? [] : mergeById(prev, res.content ?? []),
        );
        setBranchesPage(page);
        setBranchesHasNext(Boolean(res.hasNext));
        setBranchesLoaded(true);
      } catch (err) {
        console.error("Failed to load branches", err);
      } finally {
        if (gen === branchesFetchGen.current) setLoadingBranches(false);
      }
    },
    [loadingBranches],
  );

  const fetchNegotiatorsPage = useCallback(
    async (page: number) => {
      if (loadingNegotiators) return;
      const gen = ++negotiatorsFetchGen.current;
      setLoadingNegotiators(true);
      try {
        const res = await usersService.listNegotiators(
          { legalEntityId: legalEntityId ?? undefined },
          { page, size: DROPDOWN_PAGE_SIZE },
        );
        if (gen !== negotiatorsFetchGen.current) return;
        setNegotiators((prev) =>
          page === 0 ? res.content ?? [] : mergeById(prev, res.content ?? []),
        );
        setNegotiatorsPage(page);
        setNegotiatorsHasNext(Boolean(res.hasNext));
        setNegotiatorsLoaded(true);
      } catch (err) {
        console.error("Failed to load negotiators", err);
      } finally {
        if (gen === negotiatorsFetchGen.current) setLoadingNegotiators(false);
      }
    },
    [legalEntityId, loadingNegotiators],
  );

  const fetchContactsPage = useCallback(
    async (query: string, page: number) => {
      if (loadingContacts) return;
      const gen = ++contactsFetchGen.current;
      const q = query.trim();
      setContactSearchQuery(q);
      setLoadingContacts(true);
      try {
        const res = await contactsService.list(
          { ...contactPropertyListScope(legalEntityId, branchId), ...(q ? { search: q } : {}) },
          { page, size: DROPDOWN_PAGE_SIZE },
        );
        if (gen !== contactsFetchGen.current) return;
        const items = res.content ?? [];
        setContactsPool((prev) => {
          const pinned = prev.filter((c) => ownerContactIdsRef.current.includes(c.id));
          return page === 0 ? mergeById(pinned, items) : mergeById(prev, items);
        });
        setContactsPage(page);
        setContactsHasNext(Boolean(res.hasNext));
        setContactsLoaded(true);
      } catch (err) {
        console.error("Failed to load contacts", err);
      } finally {
        if (gen === contactsFetchGen.current) setLoadingContacts(false);
      }
    },
    [legalEntityId, branchId, loadingContacts],
  );

  const handleBranchOpen = useCallback(() => {
    if (!branchesLoaded && !loadingBranches) void fetchBranchesPage(0);
  }, [branchesLoaded, fetchBranchesPage, loadingBranches]);

  const handleBranchEndReached = useCallback(() => {
    if (loadingBranches || !branchesHasNext) return;
    void fetchBranchesPage(branchesPage + 1);
  }, [branchesHasNext, branchesPage, fetchBranchesPage, loadingBranches]);

  const handleNegotiatorOpen = useCallback(() => {
    if (!negotiatorsLoaded && !loadingNegotiators) void fetchNegotiatorsPage(0);
  }, [fetchNegotiatorsPage, loadingNegotiators, negotiatorsLoaded]);

  const handleNegotiatorEndReached = useCallback(() => {
    if (loadingNegotiators || !negotiatorsHasNext) return;
    void fetchNegotiatorsPage(negotiatorsPage + 1);
  }, [fetchNegotiatorsPage, loadingNegotiators, negotiatorsHasNext, negotiatorsPage]);

  const handleOwnerSearch = useCallback(
    (query: string) => {
      const q = query.trim();
      if (q === contactSearchQuery && contactsLoaded) return;
      void fetchContactsPage(q, 0);
    },
    [contactSearchQuery, contactsLoaded, fetchContactsPage],
  );

  const handleOwnerEndReached = useCallback(() => {
    if (loadingContacts || !contactsHasNext) return;
    void fetchContactsPage(contactSearchQuery, contactsPage + 1);
  }, [contactSearchQuery, contactsHasNext, contactsPage, fetchContactsPage, loadingContacts]);

  const handleOwnerOpen = useCallback(() => {
    if (!contactsLoaded && !loadingContacts) void fetchContactsPage("", 0);
  }, [contactsLoaded, fetchContactsPage, loadingContacts]);

  const ownerOptions = useMemo(
    () => buildOwnerSelectOptions(contactsPool, ownerContactIds),
    [contactsPool, ownerContactIds],
  );

  useEffect(() => {
    if (!open) {
      hydratedOwnerIdsRef.current.clear();
      return;
    }
    const missing = ownerContactIds
      .map((id) => id.trim())
      .filter(
        (id) =>
          id &&
          !contactsPool.some((c) => c.id === id) &&
          !hydratedOwnerIdsRef.current.has(id),
      );
    for (const id of missing) {
      hydratedOwnerIdsRef.current.add(id);
      contactsService
        .getOne(id, legalEntityId ?? undefined)
        .then((raw) => {
          setContactsPool((prev) =>
            mergeById(prev, [contactDetailsToSummary(raw, id)]),
          );
        })
        .catch(() => {
          hydratedOwnerIdsRef.current.delete(id);
        });
    }
  }, [open, ownerContactIds, contactsPool, legalEntityId]);

  const selectedBranchId = watch("branchId");
  const prevBranchIdRef = useRef(selectedBranchId);

  useEffect(() => {
    if (prevBranchIdRef.current && selectedBranchId !== prevBranchIdRef.current) {
      reset({ ...emptyPropertyFormValues(), branchId: selectedBranchId });
      setOwnerContactIds([""]);
    }
    prevBranchIdRef.current = selectedBranchId;
  }, [selectedBranchId, reset]);

  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.name?.trim() || b.id })),
    [branches],
  );

  const negotiatorOptions = useMemo(
    () =>
      negotiators
        .map((u) => ({
          value: u.id,
          label: u.name?.trim() || u.email?.trim() || u.externalId?.trim() || u.id,
        }))
        .filter((o) => o.value),
    [negotiators],
  );

  const handleOwnerChange = (index: number, contactId: string) => {
    setOwnerContactIds((prev) => {
      const next = [...prev];
      next[index] = contactId;
      return next;
    });
    if (contactId && !contactsPool.some((c) => c.id === contactId)) {
      contactsService
        .getOne(contactId, legalEntityId ?? undefined)
        .then((raw) => {
          setContactsPool((prev) =>
            mergeById(prev, [contactDetailsToSummary(raw, contactId)]),
          );
        })
        .catch(() => undefined);
    }
  };

  const onCreateProperty = async (values: PropertyFormValues) => {
    if (!legalEntityId) {
      toast.error("Select a legal entity before creating a property");
      return;
    }

    const validation = validatePropertyForm(values, ownerContactIds, { isCreate: true });
    if (!validation.success) {
      return;
    }

    const resolved = resolvePropertyApiContext({
      values,
      legalEntityId,
      ownerContactIds,
      branches,
      negotiators,
      contacts: contactsPool,
    });
    if (!resolved.success) {
      toast.error(resolved.message);
      return;
    }

    setLoading(true);
    try {
      let body: Record<string, unknown>;
      try {
        body = buildPropertyCreatePayload(values, resolved.ctx);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Invalid property data");
        return;
      }
      const res = await propertiesService.create(body);
      const summary = propertyResponseToSummary(unwrapApiData(res));
      if (!summary?.id) {
        toast.error("Property created but response was incomplete");
        return;
      }
      toast.success("Property created successfully");
      onSaved?.();
      handleClose();
    } catch (err) {
      showActionErrorFromUnknown(err, "Failed to create property");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClick = () => {
    setSubmitAttempted(true);
    void handleSubmit(onCreateProperty)();
  };

  return (
    <Drawer
        open={open}
        onClose={handleClose}
        title="Add New Property"
        subtitle="Basic details, address, ownership, and lease"
        width="2xl"
        actions={
          <div className="flex w-full items-center justify-end gap-2">
            <Button variant="ghost" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="button"
              loading={loading}
              disabled={loading}
              onClick={handleCreateClick}
            >
              Add property
            </Button>
          </div>
        }
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto">
            <form
              id="property-create-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateClick();
              }}
              className="rounded-md border border-bridge-border/40 bg-white p-4 shadow-sm sm:p-6"
            >
              <PropertyFormFields
                isCreate
                submitAttempted={submitAttempted}
                register={register}
                watch={watch}
                setValue={setValue}
                ownerContactIds={ownerContactIds}
                branchOptions={branchOptions}
                loadingBranches={loadingBranches}
                onBranchOpen={handleBranchOpen}
                onBranchEndReached={handleBranchEndReached}
                negotiatorOptions={negotiatorOptions}
                loadingNegotiators={loadingNegotiators}
                onNegotiatorOpen={handleNegotiatorOpen}
                onNegotiatorEndReached={handleNegotiatorEndReached}
                ownerOptions={ownerOptions}
                loadingContacts={loadingContacts}
                onOwnerChange={handleOwnerChange}
                onAddOwner={() => setOwnerContactIds((prev) => [...prev, ""])}
                onRemoveOwner={(index) =>
                  setOwnerContactIds((prev) =>
                    prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
                  )
                }
                onOwnerOpen={handleOwnerOpen}
                onOwnerEndReached={handleOwnerEndReached}
                onContactSearch={handleOwnerSearch}
              />
            </form>
          </div>
        </div>
    </Drawer>
  );
}
